import React, { useEffect, useState } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import { MdSave } from "react-icons/md";
import { notify } from "utils";
import { useSelector, useDispatch } from "react-redux";
import Loader from "components/UI/Loader";
import { getStages, updateStages } from "actions/WorkFlowActions";
import StageList from "../DrangAndDrop/StageList";
import { ModalConfirm } from "./ModalConfirm";
import EmptyData from "assets/animations/empty-data.json";
import Lottie from "lottie-react";

const ModalHeader = ({ title, onClose, isLoading }) => (
  <div className="h-20 w-full flex flex-row justify-between items-center px-5 bg-black rounded-t-lg">
    <span className="text-white font-[Gravity] text-2xl font-bold">
      {title}
    </span>
    {isLoading ? (
      <div className="pt-2">
        <svg
          class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    ) : (
      <IoCloseCircleOutline
        onClick={onClose}
        className="text-white text-4xl cursor-pointer"
      />
    )}
  </div>
);

const ModalActions = ({ setIsNewStage, handleUpdateStages }) => (
  <div className="h-auto flex flex-row justify-center lg:justify-end  mt-5">
    <div
      onClick={() => setIsNewStage(true)}
      className="h-10 w-32 flex items-center justify-center text-white rounded-lg bg-black cursor-pointer hover:shadow-md hover:bg-[#EBC533]"
    >
      Agregar
    </div>
    <div
      onClick={() => handleUpdateStages()}
      className="h-10 w-32 flex items-center justify-center text-white rounded-lg bg-black cursor-pointer hover:shadow-md hover:bg-[#EBC533] ml-4"
    >
      Guardar
    </div>
  </div>
);

const ModalBody = ({
  stages,
  isNewStage,
  setIsNewStage,
  actions,
  setActions,
  setDuplicateName,
}) => {
  const { workFlowSelected } = useSelector((state) => state.workFlow);
  const [name, setName] = useState("");
  const [isIncident, setIsIncident] = useState(false);
  const [localStages, setLocalStages] = useState([]);

  useEffect(() => {
    const tmpStage = stages.map((item) => ({
      ...item,
      show: true,
    }));
    setLocalStages(tmpStage);
  }, [stages]);

  useEffect(() => {
    if (localStages.length > 0) {
      const localStagesFiltered = localStages.filter((item) => item.show);
      const names = localStagesFiltered.map((item) =>
        item.name.trim().toLowerCase()
      );
      const hasDuplicate = names.length !== new Set(names).size;
      setDuplicateName(hasDuplicate);
    } else {
      setDuplicateName(false);
    }
  }, [localStages, setDuplicateName]);

  const handleDelete = (item) => {
    let tmpStages = [];
    let tmpActions = { ...actions };

    if (item?.isNew) {
      tmpStages = localStages.filter((stage) => stage.id !== item.id);
      tmpActions = {
        ...actions,
        Add: actions.Add.filter((stage) => stage.id !== item.id),
      };
    } else {
      tmpStages = localStages.map((stage) => ({
        ...stage,
        show: item.id === stage.id ? false : stage.show,
      }));

      tmpActions = {
        ...actions,
        Delete: [...actions.Delete, item.id],
      };
    }

    setActions(tmpActions);
    setLocalStages(tmpStages);
  };

  const handleReset = () => {
    setName("");
    setIsIncident(false);
    setIsNewStage(false);
  };

  const handleSaveStage = () => {
    if (!name.trim()) return notify("warning", "Nombre es requerido", 3000);
    if (
      localStages.some(
        (item) => item.name.trim().toLowerCase() === name.trim().toLowerCase()
      )
    )
      return notify("warning", "Esta etapa ya existe", 3000);

    if (isIncident) {
      const hasIncidentInActionsAdd = actions.Add.find(
        (item) => item.isIncident
      );
      const hasIncidentInActionsUpd = actions.Update.find(
        (item) => item.isIncident
      );
      if (hasIncidentInActionsAdd) {
        notify("warning", "Ya existe una etapa marcada como incidencia", 3000);
        handleReset();
        return;
      }

      if (hasIncidentInActionsUpd) {
        const hasIncidenInDelete = actions.Delete.some(
          (item) => item === hasIncidentInActionsUpd.id
        );
        if (!hasIncidenInDelete) {
          notify(
            "warning",
            "Ya existe una etapa marcada como incidencia",
            3000
          );
          handleReset();
          return;
        }
      }
    }

    const id = Date.now();
    const tmpStages = [
      ...localStages,
      {
        name,
        show: true,
        isNew: true,
        id,
        isIncident,
      },
    ];
    const tmpActions = {
      ...actions,
      Add: [
        ...actions.Add,
        {
          name,
          order: localStages.filter((item) => item.show).length + 1,
          workflowTypeId: workFlowSelected.id,
          IdProject: workFlowSelected.projectId,
          id,
          isIncident,
        },
      ],
    };

    const hasIncident = tmpStages.filter(
      (item) => item.isIncident && item.show
    );
    if (hasIncident.length > 1) {
      notify("warning", "Ya existe una etapa marcada como incidencia", 3000);
      handleReset();
      return;
    }
    setActions(tmpActions);
    setLocalStages(tmpStages);
    handleReset();
  };

  const handleUpdate = (stage, name) => {
    const tmpStages = localStages.map((item) => ({
      ...item,
      name: item.id === stage.id ? name : item.name,
    }));
    let tmpActions = {};
    if (stage.isNew) {
      tmpActions = {
        ...actions,
        Add: actions.Add.some((item) => item.id === stage.id)
          ? actions.Add.map((item) => ({
              ...item,
              name: item.id === stage.id ? name : item.name,
            }))
          : [
              ...actions.Add,
              {
                id: stage.id,
                name,
                order: stage.order,
                isIncident: stage.isIncident || false,
              },
            ],
      };
    } else {
      tmpActions = {
        ...actions,
        Update: actions.Update.some((item) => item.id === stage.id)
          ? actions.Update.map((item) => ({
              ...item,
              name: item.id === stage.id ? name : item.name,
            }))
          : [
              ...actions.Update,
              {
                id: stage.id,
                name,
                order: stage.order,
                isIncident: stage.isIncident || false,
              },
            ],
      };
    }

    setActions(tmpActions);
    setLocalStages(tmpStages);
  };

  const handleUpdateIsIncidence = (stage, isIncident) => {
    if (isIncident === true) {
      if (stage?.polls && stage.polls.length > 1)
        return notify(
          "warning",
          "No es posible asignar esta etapa como incidencia porque contiene más de un sondeo.",
          3000
        );

      const hasIncident = localStages.some(
        (item) => item.isIncident && item.show
      );
      if (hasIncident) {
        notify("warning", "Ya existe una etapa marcada como incidencia", 3000);
        return;
      }
    }

    const tmpStages = localStages.map((item) => ({
      ...item,
      isIncident: item.id === stage.id ? isIncident : item.isIncident,
    }));

    let tmpActions = {};
    if (stage.isNew) {
      tmpActions = {
        ...actions,
        Add: actions.Add.some((item) => item.id === stage.id)
          ? actions.Add.map((item) => ({
              ...item,
              isIncident: item.id === stage.id ? isIncident : item.isIncident,
            }))
          : [
              ...actions.Add,
              {
                id: stage.id,
                name: stage.name,
                isIncident,
                order: stage.order,
              },
            ],
      };
    } else {
      tmpActions = {
        ...actions,
        Update: actions.Update.some((item) => item.id === stage.id)
          ? actions.Update.map((item) => ({
              ...item,
              isIncident: item.id === stage.id ? isIncident : item.isIncident,
            }))
          : [
              ...actions.Update,
              {
                id: stage.id,
                name: stage.name,
                isIncident,
                order: stage.order,
              },
            ],
      };
    }

    setActions(tmpActions);
    setLocalStages(tmpStages);
  };

  const getActions = (newStages) => {
    let tmpActions = {
      Add: [],
      Update: [],
    };

    newStages.forEach((stage, index) => {
      if (stage.isNew) {
        tmpActions.Add.push({
          ...stage,
          order: index + 1,
        });
      } else {
        tmpActions.Update.push({
          ...stage,
          order: index + 1,
        });
      }
    });

    return tmpActions;
  };

  const handleChangeOrder = (newStages) => {
    const tmpActions = getActions(newStages);

    setActions({
      ...actions,
      Update: tmpActions.Update,
      Add: tmpActions.Add,
    });
    setLocalStages(newStages);
  };

  if (localStages.length === 0 && isNewStage === false)
    return (
      <div className="w-full h-96 mt-5 flex justify-center items-center rounded-lg border-2 border-gray-500 border-dotted">
        <Lottie
          animationData={EmptyData}
          loop={true}
          autoplay={true}
          className="w-96 h-96"
        />
      </div>
    );

  return (
    <>
      <div className="h-auto w-full flex flex-row text-gray-700 font-semibold">
        <div className="w-1/5 text-center">Orden</div>
        <div className="w-8/12">Nombre</div>
        <div>Incidencia</div>
      </div>
      <div className="max-h-96 min-h-40 w-full overflow-auto">
        <StageList
          localStages={localStages}
          handleDelete={handleDelete}
          handleUpdate={handleUpdate}
          setLocalStages={setLocalStages}
          handleChangeOrder={handleChangeOrder}
          handleUpdateIsIncidence={handleUpdateIsIncidence}
        />
        {isNewStage && (
          <div className="h-14 w-full flex flex-row items-center bg-[#cecece] rounded-lg px-5 mb-2">
            <div className="w-1/5 lg:w-1/12"></div>
            <div className="w-1/5 lg:w-1/12">
              <div className="h-10 w-10 bg-[#e5e5e5] text-[#757575] rounded-lg flex items-center justify-center font-bold">
                {localStages.length + 1}
              </div>
            </div>
            <div className="w-3/5 lg:w-9/12">
              <input
                value={name}
                maxLength={50}
                onChange={({ target }) => setName(target.value)}
                type="text"
                className="h-10 w-[100%] flex items-center justify-start truncate font-bold  bg-[#e5e5e5] text-[#757575] rounded-lg px-3"
              />
            </div>
            <div className="w-1/5 lg:w-1/12 pl-14 flex flex-col text-gray-700 font-semibold">
              <input
                type="checkbox"
                className="ml-2 w-[100%] h-8 cursor-pointer"
                checked={isIncident}
                onChange={({ target }) => {
                  if (target.value === true) {
                    const hasIncident = localStages.some(
                      (item) => item.isIncident
                    );
                    if (hasIncident) {
                      notify(
                        "warning",
                        "Ya existe una etapa marcada como incidencia",
                        3000
                      );
                      return;
                    }
                  }
                  setIsIncident(target.checked);
                }}
              />
            </div>
            <div className="w-1/5 lg:w-1/12 flex justify-end">
              <div
                onClick={() => handleSaveStage()}
                className="h-10 w-10 flex justify-center items-center rounded-full bg-[#77ace9] text-[#757575] cursor-pointer hover:text-white"
              >
                <MdSave />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ModalEditStages = ({ isOpen, onClose, stages, workFlowSelected }) => {
  const dispatch = useDispatch();
  const [isNewStage, setIsNewStage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [saveModalConfirm, setSaveModalConfirm] = useState(false);
  const [isDuplicateName, setDuplicateName] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actions, setActions] = useState({
    Update: [],
    Add: [],
    Delete: [],
  });

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setShowModal(true), 10);
    } else {
      setShowModal(false);
    }
  }, [isOpen]);

  const validateActions = (actions) => {
    const hasEmptyName = (array) =>
      array.some((item) => !item.name || item.name.trim() === "");
    return hasEmptyName(actions.Add) || hasEmptyName(actions.Update);
  };

  const handleUpdateStages = async () => {
    if (isDuplicateName)
      return notify(
        "warning",
        "No se puede guardar porque hay nombres duplicados.",
        3000
      );
    setShowModalConfirm(false);
    let tmpActions = { ...actions };
    if (
      tmpActions.Add.length === 0 &&
      tmpActions.Delete.length === 0 &&
      tmpActions.Update.length === 0
    )
      return notify("warning", "No se encontraron cambios en las etapas", 3000);

    if (tmpActions.Update.length > 0 && tmpActions.Delete.length > 0) {
      tmpActions.Update = tmpActions.Update.filter(
        (item) => !tmpActions.Delete.includes(item.id)
      );
    }

    const modifiedActions = {
      ...actions,
      Add: tmpActions.Add.map(({ id, isNew, show, isIncident, ...rest }) => ({
        ...rest,
        workflowTypeId: workFlowSelected.id,
        idProject: workFlowSelected.projectId,
        IsIncident: isIncident || false,
      })),
      Update: tmpActions.Update.map(({ isNew, show, isIncident, ...rest }) => ({
        ...rest,
        IsIncident: isIncident || false,
      })),
      Delete: tmpActions.Delete.filter(
        (item, index, self) => self.indexOf(item) === index
      ),
    };
    if (validateActions(modifiedActions))
      return notify("warning", "Algunos campos están vacios", 3000);
    setIsLoading(true);
    const response = await updateStages(modifiedActions);
    if (response) {
      dispatch(getStages(workFlowSelected.id));
    }
    setIsLoading(false);
  };

  const handleValidateClose = () => {
    if (
      (actions.Add.length === 0) & (actions.Delete.length === 0) &&
      actions.Update.length === 0
    )
      return onClose();
    setShowModalConfirm(true);
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-xl shadow-lg w-full max-w-7xl h-[60vh] relative transition-all duration-300 transform ${
          showModal ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        <div className="h-full overflow-y-auto">
          <ModalHeader
            isLoading={isLoading}
            title={"Editar Etapas"}
            onClose={handleValidateClose}
          />
          <div className="lg:px-10 sm:px-0">
            {isLoading ? (
              <Loader />
            ) : (
              <>
                <ModalActions
                  setIsNewStage={setIsNewStage}
                  handleUpdateStages={() => setSaveModalConfirm(true)}
                />
                <ModalBody
                  stages={stages}
                  isNewStage={isNewStage}
                  setIsNewStage={setIsNewStage}
                  actions={actions}
                  setActions={setActions}
                  setDuplicateName={setDuplicateName}
                />
              </>
            )}
          </div>
        </div>
        {showModalConfirm && (
          <ModalConfirm
            onClose={onClose}
            onAcept={handleUpdateStages}
            title={"Advertencia"}
            description={
              "Tienes cambios pendientes por guardar, ¿Estás seguro de salirte?"
            }
          />
        )}
        {saveModalConfirm && (
          <ModalConfirm
            onClose={() => setSaveModalConfirm(false)}
            onAcept={handleUpdateStages}
            title={"Edición de etapas"}
            description={"¿Estás seguro de guardar estos cambios?"}
            discardDescription="Cerrar"
          />
        )}
      </div>
    </div>
  );
};

export default ModalEditStages;
